// Third-party
import React from 'react';
import round from 'lodash/round';

// Utils
import { getAssetDetailsForRiskComparison, RISK_ALLOCATION_KEYS } from '../../../../utils/comparison';
import { formatLocalizedString, FormattedMessage } from '../../../../utils/i18n';

// Components
import { HoldingIcon } from '../../../components/common';
import AssetAllocationDisclaimer from '../../../components/disclaimers/comparison/AssetAllocationDisclaimer';
import ComparisonItem from '@/types/comparison-item';
import { TertiaryButton } from '@/views/components/buttons';

type Props = {
  item: ComparisonItem;
  showAssetDetails?: boolean;
  splitCashAndFixedIncome?: boolean;
  onDeselect?: () => void;
};

const SingleItemView = ({ item, showAssetDetails, splitCashAndFixedIncome, onDeselect }: Props) => {
  const allocations = getAssetDetailsForRiskComparison(item);

  const formatAllocation = (key: string) => {
    const value = allocations[key] || 0;
    return `${round(value, 2)}%`;
  };

  return (
    <div className="single-item-view">
      <div className="single-item-header-container">
        <div className="item-header">
          <HoldingIcon color={item.color} type={item.type} />
          <span>{formatLocalizedString(item.name)}</span>
        </div>

        <TertiaryButton className="button-deselect" onClick={onDeselect}>
          <i className="icon-minus-med" />
          <FormattedMessage id="Common.Deselect" />
        </TertiaryButton>
      </div>

      <div className="holdings-header d-flex">
        <div className="column holding" />

        {showAssetDetails && (
          <>
            {splitCashAndFixedIncome ? (
              <>
                <div className="column cash">
                  <FormattedMessage id={`RiskProfile.${RISK_ALLOCATION_KEYS.CASH}`} />
                </div>

                <div className="column fixed-income">
                  <FormattedMessage id={`RiskProfile.${RISK_ALLOCATION_KEYS.FIXED_INCOME}`} />
                </div>
              </>
            ) : (
              <div className="column cash-fixed-income">
                <FormattedMessage id="RiskProfile.CASH_FIXED_INCOME" />
              </div>
            )}

            <div className="column equity">
              <FormattedMessage id={`RiskProfile.${RISK_ALLOCATION_KEYS.EQUITY}`} />
            </div>

            <div className="column other">
              <FormattedMessage id={`RiskProfile.${RISK_ALLOCATION_KEYS.OTHER}`} />
            </div>
          </>
        )}
      </div>

      <div className="item d-flex align-items-center">
        <div className="column holding">
          <div className="symbol d-flex">
            <span>{item.holdings[0].symbol}</span>
          </div>

          <div className="item-name flex-fill">
            <span>{formatLocalizedString(item.name)}</span>
          </div>
        </div>

        {showAssetDetails && (
          <>
            <div className="column fixed-income">
              <span>{formatAllocation(RISK_ALLOCATION_KEYS.FIXED_INCOME)}</span>
            </div>

            <div className="column equity">
              <span>{formatAllocation(RISK_ALLOCATION_KEYS.EQUITY)}</span>
            </div>

            <div className="column other">
              <span>{formatAllocation(RISK_ALLOCATION_KEYS.OTHER)}</span>
            </div>
          </>
        )}
      </div>

      <AssetAllocationDisclaimer items={[item]} />
    </div>
  );
};

export default SingleItemView;
